import { z } from "zod";
import i18n from "@bcc/lib/i18n";
import { INTENSITY_MAX, INTENSITY_MIN } from "@shared/types/intensity";
import {
  DEFAULT_DESCRIPTION_MAX_LENGTH,
  DEFAULT_MAX_ELEVATION,
  DEFAULT_MIN_ELEVATION,
  DEFAULT_ROASTER_NOTE_MAX_LENGTH,
  DEFAULT_TITLE_MAX_LENGTH,
  MAX_BADGES_SELECTION,
} from "../constants";

const V = "views.shopifyImportModal.productForm.validation" as const;

// Schema for upload image (URL or file)
const uploadImageSchema = z.union([
  z.object({
    type: z.literal("url"),
    url: z.string(),
    thumbnailUrl: z.string().optional(),
    width: z.number().optional(),
    height: z.number().optional(),
  }),
  z.object({
    type: z.literal("file"),
    file: z.unknown(),
    width: z.number().optional(),
    height: z.number().optional(),
  }),
]);

export interface ProductFieldsSchemaConfig {
  imageOptional?: boolean;
  /**
   * Title character limit. Must stay in step with the `titleMaxLength` the form
   * renders in its character counter, otherwise the counter and the validation
   * can disagree (counter says `49/100` while zod still rejects at 40).
   */
  titleMaxLength?: number;
  /** New Bag Size mode: product-level fields are read-only, prefilled from the
   *  parent SKU, and unfixable by the roaster — skip their content validation
   *  so a sparse parent (e.g. a PIM-only product missing flavor/roast fields)
   *  can't permanently disable submit. Mirrors buildImportedProductSchema's
   *  rationale for the Shopify import flow, but keeps the variant-level and
   *  cross-field rules (whole-bean per bag size) strict. */
  readOnlyProductFields?: boolean;
  /** Feature_Badges flag: when false, the Badges field is hidden from the UI (see
   *  product-form.tsx), so its `MAX_BADGES_SELECTION` cap must not be enforced —
   *  a product that already exceeds it (existing data, or the flag toggled off
   *  after selection) would otherwise fail validation with no field the roaster
   *  can see to fix, permanently blocking submit. */
  badgesEnabled?: boolean;
}

// Factory for product-level fields schema with configurable image requirement
export const createProductFieldsSchema = ({
  imageOptional = false,
  readOnlyProductFields = false,
  titleMaxLength = DEFAULT_TITLE_MAX_LENGTH,
  badgesEnabled = false,
}: ProductFieldsSchemaConfig = {}) =>
  z.object({
    // Required text fields with max length
    title: readOnlyProductFields
      ? z.string()
      : z
          .string()
          .min(
            1,
            i18n.t(`${V}.required`, {
              field: i18n.t("views.shopifyImportModal.productForm.labels.title"),
            })
          )
          .max(
            titleMaxLength,
            i18n.t(`${V}.maxLength`, {
              field: i18n.t("views.shopifyImportModal.productForm.labels.title"),
              max: titleMaxLength,
            })
          ),

    description: readOnlyProductFields
      ? z.string()
      : z
          .string()
          .min(
            1,
            i18n.t(`${V}.required`, {
              field: i18n.t("views.shopifyImportModal.productForm.labels.description"),
            })
          )
          .max(
            DEFAULT_DESCRIPTION_MAX_LENGTH,
            i18n.t(`${V}.maxLength`, {
              field: i18n.t("views.shopifyImportModal.productForm.labels.description"),
              max: DEFAULT_DESCRIPTION_MAX_LENGTH,
            })
          ),

    roasterNotes: readOnlyProductFields
      ? z.string()
      : z
          .string()
          .min(
            1,
            i18n.t(`${V}.required`, {
              field: i18n.t("views.shopifyImportModal.productForm.labels.roasterNotes"),
            })
          )
          .max(
            DEFAULT_ROASTER_NOTE_MAX_LENGTH,
            i18n.t(`${V}.maxLength`, {
              field: i18n.t("views.shopifyImportModal.productForm.labels.roasterNotes"),
              max: DEFAULT_ROASTER_NOTE_MAX_LENGTH,
            })
          ),

    // Image uploads — required by default, optional when imageOptional is true
    imageUploads: imageOptional
      ? z.array(uploadImageSchema).nullable().optional()
      : z
          .array(uploadImageSchema)
          .min(
            1,
            i18n.t(`${V}.minItems`, {
              field: i18n.t("views.shopifyImportModal.productForm.labels.imageUploads"),
            })
          )
          .nullable()
          .refine(
            (val): boolean => val !== null,
            i18n.t(`${V}.minItems`, {
              field: i18n.t("views.shopifyImportModal.productForm.labels.imageUploads"),
            })
          ),
    // Required single-select fields
    mainFlavorCategory: readOnlyProductFields
      ? z.string()
      : z.string().min(
          1,
          i18n.t(`${V}.required`, {
            field: i18n.t("views.shopifyImportModal.productForm.labels.mainFlavorCategory"),
          })
        ),
    secondaryFlavorCategory: readOnlyProductFields
      ? z.string()
      : z.string().min(
          1,
          i18n.t(`${V}.required`, {
            field: i18n.t("views.shopifyImportModal.productForm.labels.secondaryFlavorCategory"),
          })
        ),
    productType: readOnlyProductFields
      ? z.string()
      : z.string().min(
          1,
          i18n.t(`${V}.required`, {
            field: i18n.t("views.shopifyImportModal.productForm.labels.productType"),
          })
        ),
    roastLevel: readOnlyProductFields
      ? z.string()
      : z.string().min(
          1,
          i18n.t(`${V}.required`, {
            field: i18n.t("views.shopifyImportModal.productForm.labels.roastLevel"),
          })
        ),

    // Optional single-select fields (not mandatory per PR #687 review)
    process: z.string(),
    varietal: z.string(),
    region: z.string(),
    country: z.string(),

    // Cross-border ship-to is bag-size based — it lives on the
    // variant (see variant-schema.ts), entered on the whole-bean variant and
    // inherited by other grinds of the same bag size. No product-level field.

    // Required single-select fields

    // Boolean field
    isDecaf: z.boolean(),

    // Multi-select arrays with constraints
    tastingNotes: readOnlyProductFields
      ? z.array(z.string())
      : z
          .array(z.string())
          .min(
            1,
            i18n.t(`${V}.minItems`, {
              field: i18n.t("views.shopifyImportModal.productForm.labels.tastingNotes"),
            })
          )
          .max(
            3,
            i18n.t(`${V}.maxItems`, {
              field: i18n.t("views.shopifyImportModal.productForm.labels.tastingNotes"),
              max: 3,
            })
          ),

    brewMethod: readOnlyProductFields
      ? z.array(z.string())
      : z.array(z.string()).min(
          1,
          i18n.t(`${V}.minItems`, {
            field: i18n.t("views.shopifyImportModal.productForm.labels.brewMethod"),
          })
        ),

    // Optional multi-select (can be null or empty)
    certifications: z.array(z.string()).nullable(),
    badges: badgesEnabled
      ? z
          .array(z.string())
          .max(
            MAX_BADGES_SELECTION,
            i18n.t(`${V}.maxItems`, {
              field: i18n.t("views.shopifyImportModal.productForm.labels.badges"),
              max: MAX_BADGES_SELECTION,
            })
          )
          .nullable()
          .optional()
      : z.array(z.string()).nullable().optional(),
    // Optional single-select; absent on drafts saved before Character existed
    character: z.string().optional(),
    // Elevation fields (500-3000) — not required in UI, but form always provides
    // numeric defaults so the schema keeps them as required numbers with range checks
    elevationStart: readOnlyProductFields
      ? z.number()
      : z
          .number()
          .min(
            DEFAULT_MIN_ELEVATION,
            i18n.t("views.shopifyImportModal.productForm.helpers.elevationRange", {
              min: DEFAULT_MIN_ELEVATION,
              max: DEFAULT_MAX_ELEVATION,
            })
          )
          .max(
            DEFAULT_MAX_ELEVATION,
            i18n.t("views.shopifyImportModal.productForm.helpers.elevationRange", {
              min: DEFAULT_MIN_ELEVATION,
              max: DEFAULT_MAX_ELEVATION,
            })
          ),
    elevationEnd: readOnlyProductFields
      ? z.number()
      : z
          .number()
          .min(
            DEFAULT_MIN_ELEVATION,
            i18n.t("views.shopifyImportModal.productForm.helpers.elevationRange", {
              min: DEFAULT_MIN_ELEVATION,
              max: DEFAULT_MAX_ELEVATION,
            })
          )
          .max(
            DEFAULT_MAX_ELEVATION,
            i18n.t("views.shopifyImportModal.productForm.helpers.elevationRange", {
              min: DEFAULT_MIN_ELEVATION,
              max: DEFAULT_MAX_ELEVATION,
            })
          ),

    // Intensity score. Optional throughout — a roaster who skips the section
    // can still import. Only an out-of-range value is rejected.
    //
    // Deliberately NOT gated on `readOnlyProductFields`, unlike every field
    // above. That flag exists for product-level fields that are prefilled from
    // PIM and rendered disabled, where content rules would strand the roaster
    // with an unfixable form. The Intensity section is the opposite case: it is
    // mounted outside the locked-fields wrapper and stays editable for imported
    // products.
    intensityPosition: z
      .number()
      .int(
        i18n.t("views.shopifyImportModal.productForm.helpers.intensityRange", {
          min: INTENSITY_MIN,
          max: INTENSITY_MAX,
        })
      )
      .min(
        INTENSITY_MIN,
        i18n.t("views.shopifyImportModal.productForm.helpers.intensityRange", {
          min: INTENSITY_MIN,
          max: INTENSITY_MAX,
        })
      )
      .max(
        INTENSITY_MAX,
        i18n.t("views.shopifyImportModal.productForm.helpers.intensityRange", {
          min: INTENSITY_MIN,
          max: INTENSITY_MAX,
        })
      )
      .nullable()
      .optional(),
    // Derived from the position, never entered directly. Kept as a plain
    // string like every other select in this schema — no z.enum.
    intensityZone: z.string().optional(),
    // The "most similar to" answer the position was derived from. See `Product.closestMatchCoffee`.
    closestMatchCoffee: z.string().nullable().optional(),
  });

// Default schema (images required) — backwards compatible
export const productFieldsSchema = createProductFieldsSchema();
